import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone } from "lucide-react";
import { scrollToForm } from "@/App";

const PlusMinus = ({ open }) => (
  <span className="relative w-5 h-5 shrink-0" aria-hidden="true">
    <span className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[2px] bg-gdark" />
    <span
      className={`absolute inset-y-0 left-1/2 -translate-x-1/2 w-[2px] bg-gdark transition-transform duration-300 ${
        open ? "rotate-90" : ""
      }`}
    />
  </span>
);

const FAQ = [
  {
    category: "The 2027 rules",
    items: [
      {
        q: "Do I have to replace my gas hot water system by March 2027?",
        a: "No. A working gas system can stay. From 1 March 2027, if an existing gas hot water system fails beyond repair, it must be replaced with an electric alternative such as a heat pump. Planning ahead means you choose the timing instead of deciding in an emergency.",
      },
      {
        q: "Can I still get my gas system repaired?",
        a: "Yes. The requirement only applies when a system can't be repaired.",
      },
      {
        q: "I rent or I'm a landlord. What applies to me?",
        a: "From 1 March 2027, when a hot water system in a rental reaches end of life, the rental provider must replace it with an efficient electric option such as a heat pump. Replacing the system is the rental provider's responsibility, not the tenant's.",
      },
    ],
  },
  {
    category: "Rebates & payment",
    items: [
      {
        q: "How much will it cost?",
        a: "Every home is different, so we give you a fixed price for your home. Answer a few quick questions and we'll confirm which rebates apply and send your price, with no obligation.",
      },
      {
        q: "What rebates can I get, and can I combine them?",
        a: "Eligible owner-occupiers can combine a Solar Victoria rebate with a Victorian Energy Upgrades discount and federal STCs. We check your eligibility and handle the paperwork for you.",
      },
      {
        q: "My household earns over $150,000. Do I still get a discount?",
        a: "Yes. The income limit only applies to the Solar Victoria rebate. The Victorian Energy Upgrades discount and federal STCs can still apply.",
      },
      {
        q: "Can I pay in instalments?",
        a: "Yes. Through Brighte, eligible homeowners can spread the cost of a heat pump over 12 or 18 months with a 0% interest payment plan, paid fortnightly. A small deposit is paid to C2H first. Fees apply, including an establishment fee and a weekly account keeping fee, and approval is subject to Brighte's credit criteria.",
      },
      {
        q: "Who handles the finance application?",
        a: "Brighte. You apply and get verified through your Brighte account, and Brighte answers any questions about your payment plan. Once Brighte confirms, we book your install.",
      },
    ],
  },
  {
    category: "Your install",
    items: [
      {
        q: "How long does installation take?",
        a: "A standard replacement is usually completed in one day, and your hot water is back on the same day.",
      },
      {
        q: "My hot water has stopped working. How fast can you help?",
        a: "Call us on 0494 686 355. We can usually restore hot water within about 48 hours.",
      },
      {
        q: "Will a heat pump work in a Melbourne winter, and is it noisy?",
        a: "Yes. Modern heat pumps are designed for Melbourne conditions. They make a low hum similar to an air conditioner, and we place them away from bedrooms where possible.",
      },
      {
        q: "Which brands do you install?",
        a: "Rinnai. We'll recommend the right size and model for your household.",
      },
      {
        q: "Do you install in apartments?",
        a: "We install in houses, townhouses and units with suitable outdoor space. We don't currently install in apartments.",
      },
      {
        q: "Which areas do you cover?",
        a: "We install across Greater Melbourne.",
      },
      {
        q: "What warranty do I get?",
        a: "Manufacturer warranties vary by brand and component, and we'll explain exactly what's covered in your quote.",
      },
    ],
  },
];

const Faq = () => {
  const [tab, setTab] = useState(0);
  const [open, setOpen] = useState(0);

  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.flatMap((cat) =>
        cat.items.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        }))
      ),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-schema";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => script.remove();
  }, []);

  const selectTab = (i) => {
    setTab(i);
    setOpen(0);
  };

  return (
    <section
      data-testid="faq-section"
      className="bg-white py-16 md:py-24 border-t border-navy/10"
    >
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-gdark mb-4 flex items-center justify-center gap-2">
            <span className="inline-block w-8 h-px bg-gdark" />
            Got questions?
            <span className="inline-block w-8 h-px bg-gdark" />
          </p>
          <h2 className="font-serif font-black text-navy tracking-tight text-4xl md:text-5xl leading-tight">
            Everything you need to know
          </h2>
        </motion.div>

        <div
          data-testid="faq-tabs"
          className="mt-10 flex gap-3 overflow-x-auto pb-2 md:justify-center [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {FAQ.map((cat, i) => (
            <button
              key={cat.category}
              onClick={() => selectTab(i)}
              data-testid={`faq-tab-${i}`}
              aria-pressed={tab === i}
              className={`shrink-0 px-6 py-2.5 rounded-full text-sm font-bold uppercase tracking-wider border transition-[background-color,color,border-color] duration-200 ${
                tab === i
                  ? "bg-navy text-white border-navy"
                  : "bg-white text-navy border-navy/25 hover:border-amber"
              }`}
            >
              {cat.category}
            </button>
          ))}
        </div>

        <div className="mt-8 divide-y divide-navy/10 border-y border-navy/10">
          {FAQ[tab].items.map((item, i) => {
            const isOpen = open === i;
            return (
              <div key={item.q} data-testid={`faq-item-${tab}-${i}`}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`faq-question-${tab}-${i}`}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between gap-4 py-5 text-left group"
                >
                  <span className="text-lg md:text-xl font-semibold text-navy-ink group-hover:text-gdark transition-colors duration-200">
                    {item.q}
                  </span>
                  <PlusMinus open={isOpen} />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p
                        data-testid={`faq-answer-${tab}-${i}`}
                        className="pb-6 text-base md:text-lg text-navy-muted leading-relaxed max-w-3xl"
                      >
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5 }}
          data-testid="faq-contact-card"
          className="mt-10 bg-cream rounded-[20px] p-6 md:p-8 flex flex-col sm:flex-row items-center justify-between gap-5 text-center sm:text-left"
        >
          <p className="text-lg font-semibold text-navy-ink">
            Still have a question? Call us on 0494 686 355
          </p>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <a
              href="tel:+61494686355"
              data-testid="faq-call-button"
              className="inline-flex items-center justify-center gap-2 border-2 border-navy text-navy font-bold px-6 py-3 rounded-lg hover:bg-navy hover:text-white transition-colors duration-200"
            >
              <Phone className="w-4 h-4" />
              0494 686 355
            </a>
            <button
              onClick={scrollToForm}
              data-testid="faq-cta-button"
              className="inline-flex items-center justify-center bg-amber text-navy-deep font-bold px-6 py-3 rounded-lg hover:-translate-y-0.5 transition-transform duration-200"
            >
              See My Price
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Faq;
